import { useState } from "react";
import { Table2, Download, Users, Car as CarIcon, Receipt } from "lucide-react";
import { fmtCZK } from "@/portal/components/adminShared";
import { toast } from "@/components/ui/sonner";

const downloadCsv = (filename, rows) => {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = "\uFEFF" + rows.map((r) => r.map(esc).join(";")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 6000);
};

const Th = ({ children }) => <th className="text-left text-[11px] uppercase tracking-wider text-zinc-500 font-medium px-3 py-2.5">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 text-sm text-zinc-300 ${className}`}>{children}</td>;

const TablesTab = ({ clients }) => {
  const [view, setView] = useState("clients");

  const rentals = clients.flatMap((c) => (c.rentals || []).map((r) => ({ ...r, clientName: c.name })));
  const invoices = clients.flatMap((c) => (c.invoices || []).map((i) => ({ ...i, clientName: c.name })));

  const VIEWS = [
    { key: "clients", label: `Klienti (${clients.length})`, icon: Users },
    { key: "rentals", label: `Pronájmy (${rentals.length})`, icon: CarIcon },
    { key: "invoices", label: `Faktury (${invoices.length})`, icon: Receipt },
  ];

  const exportCurrent = () => {
    try {
      if (view === "clients") {
        downloadCsv("klienti.csv", [["Jméno/Firma", "Typ", "E-mail", "Telefon", "IČO", "DIČ", "Vozů"],
          ...clients.map((c) => [c.name, c.client_type === "company" ? "Firma" : "Osoba", c.email, c.phone, c.ico, c.dic, (c.rentals || []).length])]);
      } else if (view === "rentals") {
        downloadCsv("pronajmy.csv", [["Klient", "Vozidlo", "SPZ", "Od", "Do", "Nájem", "Kauce"],
          ...rentals.map((r) => [r.clientName, r.car ? `${r.car.brand} ${r.car.model}` : "", r.license_plate, r.date_from, r.date_to, r.amount, r.deposit])]);
      } else {
        downloadCsv("faktury.csv", [["Klient", "Číslo", "Typ", "Částka", "Stav", "Splatnost", "Uhrazeno"],
          ...invoices.map((i) => [i.clientName, i.number, i.invoice_type === "deposit" ? "kauce" : "nájemné", i.amount, i.status, i.due_on, i.paid_on])]);
      }
      toast.success("CSV staženo.");
    } catch (e) { toast.error("Export selhal."); }
  };

  const empty = (view === "clients" && clients.length === 0) || (view === "rentals" && rentals.length === 0) || (view === "invoices" && invoices.length === 0);

  return (
    <div data-testid="tables-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><Table2 className="w-5 h-5 text-orange-400" /> Tabulky / data</h3>
        <button onClick={exportCurrent} className="text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 inline-flex items-center gap-1.5" data-testid="tables-export">
          <Download className="w-3.5 h-3.5" /> Export CSV (Excel)
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {VIEWS.map((v) => (
          <button key={v.key} onClick={() => setView(v.key)} className={`text-sm px-4 py-2 rounded-full border inline-flex items-center gap-2 ${view === v.key ? "border-orange-500 bg-orange-500/15 text-orange-300" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`tables-view-${v.key}`}>
            <v.icon className="w-4 h-4" /> {v.label}
          </button>
        ))}
      </div>

      {empty ? (
        <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="tables-empty">Žádná data k zobrazení.</div>
      ) : (
        <div className="glass rounded-2xl overflow-x-auto" data-testid="tables-grid">
          <table className="w-full min-w-[640px]">
            {view === "clients" && (
              <>
                <thead><tr className="border-b border-white/10"><Th>Jméno / firma</Th><Th>Typ</Th><Th>E-mail</Th><Th>Telefon</Th><Th>Vozů</Th></tr></thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} className="border-b border-white/5" data-testid={`tbl-client-${c.id}`}>
                      <Td className="font-medium text-white">{c.name}</Td>
                      <Td>{c.client_type === "company" ? "Firma" : "Osoba"}</Td>
                      <Td>{c.email}</Td><Td>{c.phone || "—"}</Td><Td>{(c.rentals || []).length}</Td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
            {view === "rentals" && (
              <>
                <thead><tr className="border-b border-white/10"><Th>Klient</Th><Th>Vozidlo</Th><Th>SPZ</Th><Th>Od</Th><Th>Do</Th><Th>Nájem</Th></tr></thead>
                <tbody>
                  {rentals.map((r) => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`tbl-rental-${r.id}`}>
                      <Td className="text-white">{r.clientName}</Td>
                      <Td>{r.car ? `${r.car.brand} ${r.car.model}` : "—"}</Td>
                      <Td>{r.license_plate || "—"}</Td><Td>{r.date_from || "—"}</Td><Td>{r.date_to || "—"}</Td>
                      <Td>{fmtCZK(r.amount)}</Td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
            {view === "invoices" && (
              <>
                <thead><tr className="border-b border-white/10"><Th>Klient</Th><Th>Číslo</Th><Th>Typ</Th><Th>Částka</Th><Th>Stav</Th><Th>Splatnost</Th></tr></thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b border-white/5" data-testid={`tbl-invoice-${i.id}`}>
                      <Td className="text-white">{i.clientName}</Td>
                      <Td>{i.number || "—"}</Td>
                      <Td>{i.invoice_type === "deposit" ? "kauce" : "nájemné"}</Td>
                      <Td>{fmtCZK(i.amount)}</Td>
                      <Td>{i.status}</Td><Td>{i.due_on || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

export default TablesTab;
